"use client";

import React from "react";
import type {
  ClickedFeature,
  OverlayStyleMap,
  SelectedIndicator,
} from "@/types/public/types";
import type {
  AdditionalLayer,
  GroupLayer,
} from "@/types/models/capas";
import { applyUserTranslationsDeep, fetchElements } from "@/lib/utils";
import { USOS_SOURCE_BASE, USOS_LAYERS } from "@/types/public/constants";
import { capasService } from "@/lib/services/capas.service";
import { gruposCapasService } from "@/lib/services/grupos.service";
import { GeoGeometryCollection } from "@/types/models/geometry";
import { ProjectAU, Tenant } from "@/types/types";

export type MapState = {
  muniCode: string;
  tileBaseUrl: string;

  panelHidden: boolean;
  setPanelHidden: React.Dispatch<React.SetStateAction<boolean>>;

  clicked: ClickedFeature | null;
  setClicked: React.Dispatch<React.SetStateAction<ClickedFeature | null>>;

  selected: SelectedIndicator | null;
  setSelected: React.Dispatch<React.SetStateAction<SelectedIndicator | null>>;

  otherSources: Record<string, string>;
  setOtherSources: React.Dispatch<React.SetStateAction<Record<string, string>>>;

  overlayStyles: OverlayStyleMap;
  setOverlayStyles: React.Dispatch<React.SetStateAction<OverlayStyleMap>>;

  tenantId: number | null;
  proyectosAU: ProjectAU[];

  capasAdicionales: AdditionalLayer[];
  gruposCapas: GroupLayer[];

  auSources: Record<string, GeoGeometryCollection>;
  setAuSources: React.Dispatch<
    React.SetStateAction<Record<string, GeoGeometryCollection>>
  >;

  auOverlayStyles: OverlayStyleMap;
  setAuOverlayStyles: React.Dispatch<React.SetStateAction<OverlayStyleMap>>;

  clearSelection: () => void;
};

const MapStateContext = React.createContext<MapState | null>(null);

export function useMapState(): MapState {
  const ctx = React.useContext(MapStateContext);
  if (!ctx) throw new Error("useMapState must be used inside <MapStateProvider>");
  return ctx;
}

export function MapStateProvider({
  value,
  children,
}: {
  value: Omit<
    MapState,
    | "clearSelection"
    | "tenantId"
    | "proyectosAU"
    | "capasAdicionales"
    | "gruposCapas"
    | "auSources"
    | "setAuSources"
    | "auOverlayStyles"
    | "setAuOverlayStyles"
  >;
  children: React.ReactNode;
}) {
  const [tenantId, setTenantId] = React.useState<number | null>(null);
  const [proyectosAU, setProyectosAU] = React.useState<ProjectAU[]>([]);
  const [capasAdicionales, setCapasAdicionales] = React.useState<AdditionalLayer[]>([]);
  const [gruposCapas, setGruposCapas] = React.useState<GroupLayer[]>([]);

  const [auSources, setAuSources] = React.useState<Record<string, GeoGeometryCollection>>({});
  const [auOverlayStyles, setAuOverlayStyles] = React.useState<OverlayStyleMap>({});

  const clearSelection = React.useCallback(() => {
    value.setSelected(null);
    value.setClicked(null);
    value.setOverlayStyles({});
    value.setOtherSources({});
    setAuOverlayStyles({});
    setAuSources({});
  }, [value]);

  React.useEffect(() => {
    const usosSourceKey = `${value.muniCode}_${USOS_SOURCE_BASE}`;

    const anyUsosEnabled = USOS_LAYERS.some((x) => {
      const k = `${value.muniCode}_${x.id}`;
      return !!value.overlayStyles[k]?.enabled;
    });

    value.setOtherSources((prev) => {
      const has = prev[usosSourceKey] != null;

      if (anyUsosEnabled && !has) return { ...prev, [usosSourceKey]: usosSourceKey };
      if (!anyUsosEnabled && has) {
        const { [usosSourceKey]: _removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, [value.muniCode, value.overlayStyles, value.setOtherSources]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadTenant() {
      if (!value.muniCode) {
        setTenantId(null);
        return;
      }

      const tenants =
        (await fetchElements<Tenant>(
          "tenants",
          ["id", "codigo_municipio"],
          undefined,
          { codigo_municipio: { _eq: value.muniCode } }
        )) ?? [];

      if (cancelled) return;
      setTenantId(tenants[0]?.id ?? null);
    }

    void loadTenant();

    return () => {
      cancelled = true;
    };
  }, [value.muniCode]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadProyectos() {
      if (!tenantId) {
        setProyectosAU([]);
        return;
      }

      const data =
        (await fetchElements<ProjectAU>(
          "proyectos_au",
          ["id", "codigo", "translations.*", "mapa", "publico"],
          undefined,
          { tenant: { _eq: tenantId } }
        )) ?? [];

      if (cancelled) return;

      const localized = applyUserTranslationsDeep(data, "es-ES");
      const filtrado = localized.filter((p) => p.publico === true);

      setProyectosAU(filtrado);
    }

    void loadProyectos();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadCapasPublicas() {
      if (!tenantId) {
        setCapasAdicionales([]);
        return;
      }

      const data = await capasService.fetchByTenant({
        tenantId,
        userLang: "es-ES",
      });

      if (cancelled) return;
      setCapasAdicionales(data ?? []);
    }

    void loadCapasPublicas();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadGruposPublicos() {
      if (!tenantId) {
        setGruposCapas([]);
        return;
      }

      const data = await gruposCapasService.fetchByTenant({
        tenantId,
        userLang: "es-ES",
      });

      if (cancelled) return;
      setGruposCapas(data ?? []);
    }

    void loadGruposPublicos();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return (
    <MapStateContext.Provider
      value={{
        ...value,
        clearSelection,
        tenantId,
        proyectosAU,
        capasAdicionales,
        gruposCapas,
        auSources,
        setAuSources,
        auOverlayStyles,
        setAuOverlayStyles,
      }}
    >
      {children}
    </MapStateContext.Provider>
  );
}